/**
 * **`useLeaveGuard(dirty)`** — leaving with unsaved changes asks first
 * (C13, spec §10.5 item 3; ADR-0032 §5; interface I-1 of Wave 14).
 *
 * Ported from 12A's `useUnsavedGuard`, which 14E retired — every settings page
 * now calls this hook directly — so there is one guard in the app:
 *
 * - **A route change** is held by React Router's blocker and asked about in a
 *   `ConfirmDialog` — *Leave* or *Stay and save*.
 * - **A tab close or reload** gets the browser's own prompt, which is the only
 *   one a browser allows there.
 *
 * `dirty` is the page's own answer to *"is there anything on this screen that
 * Save would send?"*. The hook returns the dialog; the page renders it
 * anywhere (it draws nothing until a navigation is held).
 *
 * **Outside a data router the route half is skipped, not thrown.** The app
 * always runs under `createBrowserRouter`, where the blocker works; a test that
 * mounts an editor inside a plain `MemoryRouter` still renders, and loses only
 * the in-app half of the guard. `useBlocker` throws outside a data router, so
 * the choice is made by rendering one of two components rather than by calling
 * a hook conditionally.
 */

import { ConfirmDialog } from '@fiveox/ui'
import { type ReactNode, useContext, useEffect } from 'react'
import { UNSAFE_DataRouterContext, useBlocker } from 'react-router'

export function useLeaveGuard(dirty: boolean): ReactNode {
  useEffect(() => {
    if (!dirty) return
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      // The legacy spelling some browsers still want before they will ask.
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  return <LeaveGuard dirty={dirty} />
}

function LeaveGuard({ dirty }: { dirty: boolean }) {
  const dataRouter = useContext(UNSAFE_DataRouterContext)
  return dataRouter ? <BlockingGuard dirty={dirty} /> : null
}

function BlockingGuard({ dirty }: { dirty: boolean }) {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty &&
      (currentLocation.pathname !== nextLocation.pathname ||
        currentLocation.search !== nextLocation.search),
  )

  return (
    <ConfirmDialog
      open={blocker.state === 'blocked'}
      onClose={() => blocker.reset?.()}
      title="Leave without saving?"
      confirmLabel="Leave"
      cancelLabel="Stay and save"
      tone="danger"
      onConfirm={() => blocker.proceed?.()}
    >
      <p className="text-muted-foreground text-sm">
        You’ve changed something on this page and haven’t saved it. If you leave now, the change is
        lost.
      </p>
    </ConfirmDialog>
  )
}
